'use client';

/**
 * Spec 08 §10a V2 — proactive deployment-drift prompt. Mounted once in
 * the root layout. Polls /api/version + listens to visibilitychange/focus.
 * Never auto-reloads; prompts the user via a sticky toast.
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

interface Props {
  current: string;
  pollIntervalMs?: number;
}

export function VersionWatcher({ current, pollIntervalMs = 5 * 60 * 1000 }: Props) {
  const latest = useRef(current);

  useEffect(() => {
    let alive = true;
    let toastShown = false;

    async function check() {
      if (!alive || document.hidden) return;
      try {
        const res = await fetch('/api/version', { cache: 'no-store' });
        if (!res.ok) return;
        const { id } = (await res.json()) as { id?: string };
        if (id && id !== latest.current && !toastShown) {
          latest.current = id;
          toastShown = true;
          toast('A new version is available.', {
            duration: Number.POSITIVE_INFINITY,
            action: {
              label: 'Refresh',
              onClick: () => window.location.reload(),
            },
          });
        }
      } catch {
        /* network blip — try again next tick */
      }
    }

    const interval = setInterval(check, pollIntervalMs);
    const onVisible = () => {
      if (!document.hidden) check();
    };
    const onFocus = () => check();

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      alive = false;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [pollIntervalMs]);

  return null;
}
